/**
 * ParticipantAudio: 원격 참여자 오디오 트랙을 숨김 audio 엘리먼트에 attach.
 * wehago-conference의 ParticipantAudio 컴포넌트 구조 기반.
 *
 * VideoTile의 <video> 엘리먼트는 muted로 렌더링하고,
 * 실제 오디오 재생은 이 컴포넌트의 숨김 <audio> 엘리먼트에서 처리한다.
 */
import React, { useEffect, useRef, useMemo } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { shallowEqual } from 'react-redux';
import { localActions } from '@/store/slices/localSlice';
import { useAllRemoteTracks } from '@/contexts/TrackContext';
import { showToast } from '@/utils/globalToast';
import {
  ECHO_RISK_MAX_SPEAKER_VOLUME,
  isEchoRiskDevicePair,
  resolveSelectedAudioDevice,
} from '@/utils/audioSafety';
import type { JitsiRemoteTrack } from '@/types/jitsi';

// 단일 오디오 트랙을 audio 엘리먼트에 attach하는 내부 컴포넌트
const AudioTrackPlayer: React.FC<{ track: JitsiRemoteTrack; outputDeviceId?: string; muted?: boolean; volume?: number }> = ({
  track,
  outputDeviceId,
  muted,
  volume,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const el = audioRef.current;
    if (!el || track.disposed !== false) return;

    try {
      track.attach(el);
    } catch { /* ignore */ }

    return () => {
      if (track.disposed === false) {
        try { track.detach(el); } catch { /* ignore */ }
      }
      el.srcObject = null;
    };
  }, [track]);

  // 스피커 뮤트 제어
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = !!muted;
  }, [muted]);

  // 오디오 출력 디바이스 변경
  useEffect(() => {
    const el = audioRef.current;
    if (!el || !outputDeviceId) return;

    // setSinkId는 일부 브라우저에서만 지원
    const elWithSink = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
    if (elWithSink.setSinkId) {
      elWithSink.setSinkId(outputDeviceId).catch(() => {});
    }
  }, [outputDeviceId]);

  // 볼륨 제어 (0~100 → 0.0~1.0)
  useEffect(() => {
    const el = audioRef.current;
    if (!el || volume == null) return;
    el.volume = Math.max(0, Math.min(1, volume / 100));
  }, [volume]);

  return (
    <audio
      ref={audioRef}
      autoPlay
      muted={!!muted}
      // 오디오 엘리먼트 자체는 숨김
      className="rtcCustoms_audioHidden"
    />
  );
};

const ParticipantAudio: React.FC = () => {
  const dispatch = useAppDispatch();
  const remoteTracks = useAllRemoteTracks();
  const audioConfig = useAppSelector((s) => ({
    audioOutputDeviceId: s.local.audioOutputDeviceId,
    audioInputDeviceId: s.local.audioInputDeviceId,
    audioInputDevices: s.local.audioInputDevices,
    audioOutputDevices: s.local.audioOutputDevices,
    speakerMuted: s.local.speakerMuted,
    speakerVolume: s.local.speakerVolume,
  }), shallowEqual);
  const participantIds = useAppSelector((s) => s.participants.participantIds);
  const localMutedIds = useAppSelector((s) => s.participants.localMutedIds);
  const echoRiskToastShownRef = useRef(false);

  const outputDeviceId = audioConfig.audioOutputDeviceId;
  const speakerMuted = audioConfig.speakerMuted;
  const speakerVolume = audioConfig.speakerVolume;

  useEffect(() => {
    const inputDevice = resolveSelectedAudioDevice(audioConfig.audioInputDevices, audioConfig.audioInputDeviceId);
    const outputDevice = resolveSelectedAudioDevice(audioConfig.audioOutputDevices, audioConfig.audioOutputDeviceId);
    const hasEchoRisk = isEchoRiskDevicePair(inputDevice, outputDevice);

    if (!hasEchoRisk) {
      echoRiskToastShownRef.current = false;
      return;
    }

    if (speakerVolume <= ECHO_RISK_MAX_SPEAKER_VOLUME) return;

    dispatch(localActions.setSpeakerVolume(ECHO_RISK_MAX_SPEAKER_VOLUME));

    if (!echoRiskToastShownRef.current) {
      showToast({
        title: '오디오 안전 모드',
        message: '하울링 위험이 있어 스피커 볼륨을 제한했습니다.',
        duration: 5000,
      });
      echoRiskToastShownRef.current = true;
    }
  }, [
    dispatch,
    audioConfig.audioInputDeviceId,
    audioConfig.audioInputDevices,
    audioConfig.audioOutputDeviceId,
    audioConfig.audioOutputDevices,
    speakerVolume,
  ]);

  // 원격 참여자의 오디오 트랙만 수집
  const audioTracks = useMemo(
    () => participantIds
      .map((id) => {
        const remoteTrack = remoteTracks[id]?.audio;
        return remoteTrack && remoteTrack.disposed === false ? { id, track: remoteTrack } : null;
      })
      .filter((t): t is { id: string; track: JitsiRemoteTrack } => t !== null),
    [participantIds, remoteTracks],
  );

  return (
    <>
      {audioTracks.map(({ id, track }) => (
        <AudioTrackPlayer
          key={id}
          track={track}
          outputDeviceId={outputDeviceId}
          // 전체 스피커 뮤트 또는 행위자 로컬 음소거 중 하나라도 해당하면 muted
          muted={speakerMuted || localMutedIds.includes(id)}
          volume={speakerVolume}
        />
      ))}
    </>
  );
};

export default ParticipantAudio;
